/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { Placement } from '../../placement';
import { Logger } from '../../../logger';
import { debugPixel, generateUUID, isGptAvailable } from '../../../util';
import { IAB_STANDARD_BANNER_SIZES } from '../../placement/constants';

import type { CampaignData } from '../../../types/';
import type { VendorFormat } from '../../../types/formats';
import type { PrebidAdUnit } from '../../../types/prebid';
import type { SiteConfig } from '../../config';

const logger = Logger.getInstance();
// Takeover buys (Adnami skins, Celtra rich-media banners) arrive as GAM line
// items rendered into an ordinary display slot, with the vendor engine
// injecting its click surfaces outside that slot's DOM — so GPT placements are
// the only ones that host them. Module-scope so the hook allocates nothing.
const SUPPORTED_VENDOR_FORMATS: readonly VendorFormat[] = ['adnami', 'celtra'];

// Ad unit path of a GPT slot, or null if the slot cannot supply one. Module
// scope so the render hot path allocates no closure per placement.
const slotAdUnitPathOf = (slot: googletag.Slot): string | null => {
  try {
    const path = slot.getAdUnitPath();
    return typeof path === 'string' && path.length > 0 ? path : null;
  } catch {
    return null;
  }
};

class GptPlacement extends Placement {
  // GAM response identifier of the last render this placement processed, used
  // to distinguish a genuine auction-less refresh render (new response) from a
  // duplicate slotRenderEnded dispatch for a render already handled. Null when
  // the event carried no identifier.
  private lastProcessedResponseIdentifier: string | null = null;
  // Ad unit path of the slot this placement is bound to, cached at bind time so
  // the render handler can test a candidate slot with one string compare rather
  // than a getter call per placement per render.
  private boundAdUnitPath: string | null = null;

  constructor(
    adUnit: PrebidAdUnit,
    adUnitTransformer: (adUnitCode: string) => string,
    campaignData: CampaignData[],
    siteConfig: SiteConfig,
  ) {
    super(adUnit, adUnitTransformer, campaignData, siteConfig);
    logger.debug(`GPT Placement created for ${this.adUnitCode}`);
  }

  protected setSlot(): void {
    if (this.slot) {
      // A publisher that "refreshes" by destroying and redefining a slot leaves
      // this reference pointing at an object GPT has dropped. Holding it means
      // every later render for this ad unit fails the identity test in the
      // render handler, so keep the reference only while GPT still knows it.
      if (this.isSlotRegistered(this.slot)) {
        return;
      }
      logger.debug(`GPT slot for ${this.adUnitCode} is no longer registered; re-resolving`);
      this.slot = null;
      this.boundAdUnitPath = null;
      this.lastProcessedResponseIdentifier = null;
    }
    if (isGptAvailable()) {
      logger.debug(`Determining GPT slot for adUnit: ${this.adUnitCode}`);
      try {
        const gptSlots = window.googletag.pubads().getSlots();

        const matchingSlot = gptSlots.find((slot) => {
          let slotElementId = slot.getSlotElementId();
          let slotAdUnitPath = slot.getAdUnitPath();
          logger.debug(`GPT slot available: ${slotAdUnitPath} (${slotElementId})`);
          return (
            this.adUnitCode == slotElementId ||
            this.adUnitCode == slotAdUnitPath ||
            slotAdUnitPath.endsWith(`/${this.adUnitCode}`)
          );
        });

        this.slot = matchingSlot || null;
        this.boundAdUnitPath = null;
        if (this.slot) {
          logger.info(`GPT slot located for ${this.adUnitCode}`);
        }
      } catch (e) {
        logger.error(`Error getting GPT slot for ${this.adUnitCode}: ${e}`);
        this.slot = null;
      }
    } else {
      logger.error(`GPT not available, unable to determine slot for adUnit: ${this.adUnitCode}`);
    }
  }

  protected getAdUnitPath(): string | null {
    if (!this.slot) {
      return null;
    }
    try {
      const path = this.slot.getAdUnitPath();
      return typeof path === 'string' && path.length > 0 ? path : null;
    } catch (e) {
      logger.error(`GPT slot failed to return ad unit path for ${this.adUnitCode}: ${e}`);
      return null;
    }
  }

  protected getSupportedVendorFormats(): readonly VendorFormat[] {
    return SUPPORTED_VENDOR_FORMATS;
  }

  protected setWrappingDomElement(): void {
    if (this.slot) {
      this.wrappingDomElement = document.getElementById(this.slot.getSlotElementId() as string);
    }
  }

  protected determineVisibility(): void {
    const size = this.getSmallestSupportedSize();
    if (!size || this.slot === null) {
      return;
    }

    let elementId: string;
    try {
      elementId = this.slot.getSlotElementId();
    } catch (e) {
      logger.error(`GPT Slot failed to return element ID: ${e}`);
      return;
    }

    const element = document.getElementById(elementId);
    if (!element) {
      logger.info(
        `GPT adunit '${this.adUnitCode}' Slot element reference '${elementId}' not found`,
      );
      return;
    }
    const isVisible = this.checkElementVisibility(element, size);
    this.isVisible(isVisible);
  }

  protected setSupportedSizes(): void {
    const iabSizes = IAB_STANDARD_BANNER_SIZES.map((s) => {
      const [w, h] = s.split('x').map(Number);
      return { width: w, height: h };
    });
    try {
      this.slot.getSizes().forEach((size: any) => {
        // GPT represents native/flex capability as the literal string 'fluid';
        // publishers also conventionally declare native slots as 1x1. Neither
        // is a renderable banner size, so they only mark native capability.
        if (size === 'fluid' || (size?.width === 1 && size?.height === 1)) {
          this.supportsNative = true;
          return;
        }
        // Only consider sizes that match a standard IAB banner size
        if (iabSizes.some((s) => s.width === size.width && s.height === size.height)) {
          this.addSupportedSize({ width: size.width, height: size.height });
        }
      });
    } catch (e) {
      logger.warn(`Error determining GPT slot size: ${e}`);
    }
  }

  protected addListeners(): void {
    logger.debug(`Adding GPT listeners for placement: ${this.adUnitCode}`);
    this.addSlotRenderedListener();
    this.addViewableImpressionListener();
    this.addPrebidAdRenderedListener();
  }

  private addSlotRenderedListener(): void {
    if (this.slot === null) {
      return;
    }
    try {
      logger.debug(`Adding slot render ended listener for GPT for: ${this.adUnitCode}`);
      const slotRenderedEventHandler = (event: googletag.events.SlotRenderEndedEvent) => {
        if (event.slot !== this.slot && !this.adoptReplacementSlot(event.slot)) {
          return;
        }
        // A slotRenderEnded for this slot means GPT has replaced (or emptied)
        // the previously rendered ad: its iframe is gone from the DOM, so the
        // listeners, impression context and latches describing it must be torn
        // down whether or not we go on to track the new render.
        this.resetStateForNewRender();
        // A render arriving with the per-opportunity latch already consumed is
        // an auction-less refresh (publisher-driven googletag refresh with no
        // Prebid auction). Mint a placement-local opportunity so the new
        // render's impression and interactions are counted; without one the
        // slot would go dark until the next Prebid auction.
        if (this.slotRenderedForOpportunity && this.isTrackableRefreshRender(event)) {
          this.mintRenderOpportunity();
        }
        if (!this.slotRenderedForOpportunity) {
          this.slotRenderedForOpportunity = true;
          logger.debug(
            `GPT slot rendered for: ${event.slot.getAdUnitPath()},` +
              ` handled by placement (${this.adUnitCode}), line: ${event.lineItemId}`,
          );

          const hasSize = Array.isArray(event.size) && event.size.length > 1;
          this.renderedSlotSize = {
            // @ts-ignore
            width: hasSize ? (event.size[0] as number) : 0,
            // @ts-ignore
            height: hasSize ? (event.size[1] as number) : 0,
          };

          if (!event.isEmpty && !event.isBackfill && event.advertiserId && event.lineItemId) {
            if (this.isBlankFriendlyRender()) {
              // Skin/takeover roadblocks blank their companion slots with 1x1
              // creatives that carry real advertiser/line ids and would
              // otherwise record as impressions. They always render 1x1 in
              // friendly iframes (the skin creative set has SafeFrame
              // disabled), while genuine fills are SafeFramed on these
              // properties, and 1x1-reporting rich formats (e.g. web
              // interstitials) are not simple friendly image frames.
              logger.info(
                `Ignoring 1x1 friendly-iframe blank render for ${this.adUnitCode}, line: ${event.lineItemId}`,
              );
              debugPixel({
                en: 'blankRenderIgnored',
                adUnitCode: this.adUnitCode,
                lineItemId: String(event.lineItemId),
              });
              return;
            }
            const advertiserId = String(event.advertiserId);
            const creativeId = event.creativeId ? String(event.creativeId) : '';
            const lineId = String(event.lineItemId);
            const renderedAdData = {
              advertiserId: advertiserId,
              lineItemId: lineId,
              creativeId: creativeId,
              isProgrammaticBuy: false,
              isPrebidAd:
                this.getSiteConfig().directLearningAdvertiserIgnoreList.includes(advertiserId),
              responseIdentifier: event.responseIdentifier
                ? String(event.responseIdentifier)
                : generateUUID(),
              size: this.renderedSlotSize,
            };
            this.lastProcessedResponseIdentifier = event.responseIdentifier
              ? String(event.responseIdentifier)
              : null;
            this.handleRenderedAdData(renderedAdData);
          } else {
            logger.debug(
              `GPT slot rendered backfill or empty: ${event.slot.getAdUnitPath()}, ${this.adUnitCode}`,
            );
            // Case of unlikely missing line ID not covered
          }
        }
      };

      window.googletag.pubads().addEventListener('slotRenderEnded', slotRenderedEventHandler);
    } catch (e) {
      logger.error(`Error adding GPT slotRenderEnded listener to ${this.adUnitCode}: ${e}`);
    }
  }

  // True when the render that just completed is a 1x1 creative in a friendly
  // (same-origin) iframe. SafeFrame or cross-origin renders fail open: if the
  // frame document is not inspectable we never suppress.
  private isBlankFriendlyRender(): boolean {
    const size = this.renderedSlotSize;
    if (!size || size.width !== 1 || size.height !== 1) {
      return false;
    }
    const frame = this.wrappingDomElement?.querySelector<HTMLIFrameElement>(
      'iframe[id^="google_ads_iframe_"]',
    );
    if (!frame) {
      return false;
    }
    try {
      return frame.contentDocument !== null;
    } catch (e) {
      return false;
    }
  }

  // Ad unit path of the currently bound slot, memoised on first use so the
  // render hot path costs a string compare rather than a getter call. Resolved
  // lazily because `slot` is not only assigned via setSlot().
  private getBoundAdUnitPath(): string | null {
    if (this.boundAdUnitPath === null && this.slot !== null) {
      this.boundAdUnitPath = slotAdUnitPathOf(this.slot);
    }
    return this.boundAdUnitPath;
  }

  // True while GPT still lists this slot. A destroyed slot keeps answering its
  // getters, so identity alone cannot tell a live slot from a dropped one.
  private isSlotRegistered(slot: googletag.Slot): boolean {
    try {
      return window.googletag.pubads().getSlots().indexOf(slot) !== -1;
    } catch (e) {
      logger.error(`Error checking GPT slot registration for ${this.adUnitCode}: ${e}`);
      // Assume still registered: dropping a live binding costs every later
      // render on this placement, while keeping a dead one costs nothing extra.
      return true;
    }
  }

  /**
   * Re-bind to the slot that replaced this placement's own, returning whether
   * the incoming render now belongs to this placement.
   *
   * Publishers that refresh by destroying and redefining a slot hand GPT a new
   * slot object for the same ad unit path. The binding is resolved once, so
   * without re-binding every post-refresh render fails the identity test in the
   * render handler and is dropped — silently, because the skipped-render
   * telemetry sits downstream of that test. The ad server counts those renders,
   * so the placement under-reports for the rest of the page's life.
   *
   * Adoption is refused while this placement's own slot is still registered: a
   * page may hold several live slots on one ad unit path (publishers reuse a
   * capped incremental name such as `incr-mpu-10`), and each must stay with the
   * placement that resolved it rather than be claimed by the first one to see a
   * render.
   */
  private adoptReplacementSlot(candidate: googletag.Slot): boolean {
    // Runs for every placement on every render, so reject on a cached string
    // compare before touching the slot registry.
    if (this.slot === null) {
      return false;
    }
    const boundPath = this.getBoundAdUnitPath();
    if (boundPath === null || slotAdUnitPathOf(candidate) !== boundPath) {
      return false;
    }
    if (this.isSlotRegistered(this.slot)) {
      return false;
    }
    logger.info(
      `GPT slot for ${this.adUnitCode} was replaced; re-binding to the new slot object`
    );
    this.slot = candidate;
    this.boundAdUnitPath = boundPath;
    // Response identifiers are scoped to the slot that produced them; carrying
    // one across would let the first render on the new slot be mistaken for a
    // duplicate and dropped.
    this.lastProcessedResponseIdentifier = null;
    this.setWrappingDomElement();
    return true;
  }

  // A refresh render only earns a minted opportunity when it would pass the
  // tracked-render predicate below AND is not a duplicate dispatch of the
  // render already processed. GAM response identifiers are unique per ad
  // response, so a legitimate re-serve of the same creative still counts; an
  // event with no identifier cannot be deduplicated and is counted.
  private isTrackableRefreshRender(event: googletag.events.SlotRenderEndedEvent): boolean {
    if (event.isEmpty || event.isBackfill || !event.advertiserId || !event.lineItemId) {
      return false;
    }
    return (
      !event.responseIdentifier ||
      String(event.responseIdentifier) !== this.lastProcessedResponseIdentifier
    );
  }

  private addViewableImpressionListener(): void {
    if (this.slot === null) {
      return;
    }
    try {
      const viewableImpressionEventHandler = (event: googletag.events.ImpressionViewableEvent) => {
        if (event.slot === this.slot) {
          logger.debug(
            `impressionViewable dispatched by GPT for: ${event.slot.getAdUnitPath()}, ${this.adUnitCode}`,
          );
          this.dispatchViewableImpression();
        }
      };
      logger.debug(`Adding impressionViewable listener for GPT for: ${this.adUnitCode}`);
      window.googletag
        .pubads()
        .addEventListener('impressionViewable', viewableImpressionEventHandler);
    } catch (e) {
      logger.error(`Error adding GPT impressionViewable listener to ${this.adUnitCode}: ${e}`);
    }
  }

  protected addAdInteractionListener(): void {
    this.addIframedAdInteractionListener();
  }

  // Interaction listeners are deliberately NOT removed per opportunity: an
  // auction is not a render, and the previously rendered ad remains clickable
  // until GPT actually replaces it. Teardown happens in resetStateForNewRender,
  // invoked from the slotRenderEnded handler above. Duplicate AdInteraction
  // dispatches from a stale window-blur listener are prevented by the
  // per-render adInteractionLoggedForRender latch, which now also survives
  // auction boundaries.
}

export { GptPlacement };
